import axios from 'axios';
import Token from '@/apis/request/token';
import { baseURL } from './baseURL';
import { redirects } from './redirect';
// import { LocalStorage } from '@/storage'
import { CheckDataType } from '@/utils';
import i18next from '@/i18n';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { error as MseeageError } from './globalMessage';

/**
 * 获取指定命名空间的 t 函数
 * @param {string} namespace - 命名空间（如 "common"）
 * @returns {Function} 绑定了该命名空间的 t 函数
 */
// export const getT = async (ns: string | string[], lng: string) => {
//     // 如果没有设置语言，则使用默认语言
//     // if (lng && i18next.resolvedLanguage !== lng) {
//     //     await i18next.changeLanguage(lng)
//     // }

//     // 这里才是动态加载语言
//     // if (ns && !i18next.hasLoadedNamespace(ns)) {
//     //     await i18next.loadNamespaces(ns)
//     // }
//     // // 返回一个新函数，自动携带命名空间参数
//     // return (key: string, options = {}) => {
//     //     // 合并用户传入的 options 和命名空间，命名空间优先级高
//     //     return i18next.t(key, { ...options, ns })
//     // }

//     return {
//         t: () => {},
//     }
// }

export const filterNull = (source: any): any => {
    if (CheckDataType.isArray(source)) {
        return (source as any[]).map((item: any) => filterNull(item));
    } else if (CheckDataType.isObject(source)) {
        const result: any = {};
        for (const key in source) {
            if (Object.prototype.hasOwnProperty.call(source, key)) {
                result[key] = filterNull(source[key]);
            }
        }
        return result;
    } else {
        return source === null ? undefined : source;
    }
};

// 定义一个常见后端请求返回
type BaseApiResponse<T> = {
    code: number;
    message: string;
    result: T;
};
// 拓展 axios 请求配置，加入我们自己的配置
interface RequestOptions {
    // 是否全局展示请求 错误信息
    globalErrorMessage?: boolean;
    // 是否全局展示请求 成功信息
    globalSuccessMessage?: boolean;

    globalErrorRedirect?: boolean;
}

// 拓展自定义请求配置
interface ExpandAxiosRequestConfig<D = any> extends AxiosRequestConfig<D> {
    interceptorHooks?: InterceptorHooks;
    requestOptions?: RequestOptions;
}

// 拓展 axios 请求配置
interface ExpandInternalAxiosRequestConfig<D = any> extends InternalAxiosRequestConfig<D> {
    interceptorHooks?: InterceptorHooks;
    requestOptions?: RequestOptions;
}

// 拓展 axios 返回配置
interface ExpandAxiosResponse<T = any, D = any> extends AxiosResponse<T, D> {
    config: ExpandInternalAxiosRequestConfig<D>;
}

export interface InterceptorHooks {
    requestInterceptor?: (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;
    requestInterceptorCatch?: (error: any) => any;
    responseInterceptor?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
    responseInterceptorHttpCatch?: (error: any) => any;
    responseInterceptorCatch?: (error: any) => any;
}

// 导出Request类，可以用来自定义传递配置来创建实例
export default class Request {
    // axios 实例
    private _instance: AxiosInstance;
    // 默认配置
    private _defaultConfig: ExpandAxiosRequestConfig = {
        // 默认请求前缀
        baseURL: '/api',
        // 默认超时时间
        timeout: 5000,
        headers: {
            'Content-Type': 'application/json',
        },
        // withCredentials: true, // 携带cookie
        requestOptions: {
            // 默认全局展示请求错误信息
            globalErrorMessage: true,
            // 默认全局展示请求成功信息
            globalSuccessMessage: true,
            // 全局重定向
            globalErrorRedirect: true,
        },
    };
    private _interceptorHooks?: InterceptorHooks;

    constructor(config: ExpandAxiosRequestConfig) {
        // 使用axios.create创建axios实例
        this._instance = axios.create(Object.assign(this._defaultConfig, config));

        this._interceptorHooks = config.interceptorHooks;
        this.setupInterceptors();
    }

    // 通用拦截，在初始化时就进行注册和运行，对基础属性进行处理
    private setupInterceptors() {
        // 添加请求拦截器
        this._instance.interceptors.request.use(this._interceptorHooks?.requestInterceptor, this._interceptorHooks?.requestInterceptorCatch);

        // 添加响应拦截器
        this._instance.interceptors.response.use(this._interceptorHooks?.responseInterceptor, this._interceptorHooks?.responseInterceptorHttpCatch);
    }

    // 定义核心请求
    public request(config: ExpandAxiosRequestConfig): Promise<AxiosResponse> {
        // 这里直接返回，不需要我们再使用 promise 封装一层
        return this._instance.request(config);
    }

    public get<T = any>(url: string, params: any, config?: ExpandAxiosRequestConfig): Promise<AxiosResponse<BaseApiResponse<T>>> {
        return this._instance.get(url, {
            params,
            ...config,
        });
    }

    public post<T = any>(url: string, params?: any, config?: ExpandAxiosRequestConfig): Promise<T> {
        return this._instance.post(url, params, config);
    }

    public put<T = any>(url: string, params?: any, config?: ExpandAxiosRequestConfig): Promise<T> {
        return this._instance.put(url, params, config);
    }

    public delete<T = any>(url: string, params: any, config?: ExpandAxiosRequestConfig): Promise<T> {
        return this._instance.delete(url, {
            params,
            ...config,
        });
    }
}

// 请求拦截器
export const transform: InterceptorHooks = {
    // 请求拦截器
    requestInterceptor: async (config: InternalAxiosRequestConfig) => {
        // const token = await Token.get(config)
        const token = await Token.get();
        // 请求头携带token
        token ? config.headers.set('Authorization', token ? `Bearer ${token}` : '') : null;

        config.headers.set('Platform-Type', 'console');

        // config.headers.set('Authorization', `Bearer ${token || ''}`);
        // 核心：按 Bearer 格式添加到 Authorization 头
        // config.headers.Authorization = `Bearer ${token}`;

        const Language = i18next.resolvedLanguage; //LocalStorage.getItem('NEXT_LOCALE')
        config.headers.set('locale', Language);

        console.log('config==', config);

        return config;
    },

    // 响应拦截器
    async responseInterceptor(result) {
        const res = result as ExpandAxiosResponse;

        // axios 返回值
        const { data, status, config } = res;

        console.log('responseInterceptor config==', config);
        console.log('responseInterceptor data==', data);

        const { requestOptions: { globalErrorMessage } = {}, url } = config;
        const { data: $data, message, code: code, ...more }: { data: any; message: string; code: keyof typeof redirects } = data;

        // // 与后端约定的请求成功码
        const SUCCESS_CODE = 0;

        // console.log('i18next==', i18next.t('common.systemError'))

        // // http状态码
        // if (status !== 200) {
        //   return Promise.reject(res)
        // }

        if (!(code == SUCCESS_CODE || code == 200)) {
            return await transform?.responseInterceptorCatch?.(res);

            // return Promise.reject(result)
        }

        // if (url == '/GetJWT/v1.0') {
        //     Token.set(resultObj)
        //     // 如果有了token，就发布队列
        //     Token.publishQueue(resultObj)
        // }

        // if (res.config.requestOptions?.globalSuccessMessage) {
        //   // 这里全局提示请求成功
        // }
        return {
            code,
            ...more,
            data: filterNull($data),
        };
    },

    // 请求前错误，
    requestInterceptorCatch(err) {
        // 请求错误，这里可以用全局提示框进行提示
        return Promise.reject(err);
    },

    // 响应 http code 错误
    async responseInterceptorHttpCatch(err = {}) {
        console.log('responseInterceptorHttpCatch err===', err);

        if (axios.isCancel(err)) {
            console.log('用户取消HTTP请求');
            return Promise.reject(err);
        }

        const { config: { requestOptions: { globalErrorMessage, globalErrorRedirect, globalSuccessMessage } = {} } = {} } = err;
        // // 与后端约定的请求成功码
        const SUCCESS_CODE = 200;
        const { response: { data: { code: code, msg } } = {} } = err;

        // 这里用来处理 http 常见错误，进行全局提示
        // const mapErrorStatus = new Map([
        //     [400, '请求方式错误'],
        //     [401, '请重新登录'],
        //     [403, '拒绝访问'],
        //     [404, '请求地址有误'],
        //     [500, '服务器出错'],
        // ])

        // const message = (err?.response?.status && mapErrorStatus.get(err?.response?.status)) || '请求出错，请稍后再试'
        // if (globalErrorMessage && msg) {
        //     MseeageError(msg || '系统错误，请稍后重试');
        // }

        // 此处全局报错
        // console.log('HTTP请求错误：', message)

        if (code !== SUCCESS_CODE) {
            return await transform?.responseInterceptorCatch?.(err.response);

            // return Promise.reject(result)
        }

        return Promise.reject(err.response);
    },

    // 响应后业务错误
    responseInterceptorCatch(result = {}) {
        const res = result as ExpandAxiosResponse;
        // axios 返回值
        const { data, status, config } = res;
        const { requestOptions: { globalErrorMessage, globalErrorRedirect } = {}, url } = config;
        const { msg: message, code: code, ...more }: { msg: string; code: keyof typeof redirects } = data;

        console.log('responseInterceptorCatch config==', config);
        console.log('responseInterceptorCatch data==', data);

        // 状态
        if (globalErrorMessage) {
            // 这里全局提示错误
            console.log('message:', message);
            MseeageError(message || '系统错误，请稍后重试');
            if (globalErrorRedirect) {
                // code && redirects[code] && redirects[code](result);
                if (code && code == 401 && redirects[code]) {
                    redirects[code](result);
                } else if (status && redirects[status]) {
                    redirects[status](result);
                }
            }
            // return Promise.reject(data)
        }
        return Promise.reject(data);
    },
};

// 具体使用时先实例一个请求对象
export const request = new Request({
    baseURL, //'https://rr-public-dev-preview-hfh0h5f8e7aygmc7.eastasia-01.azurewebsites.net/api',
    timeout: 1000 * 60,
    interceptorHooks: transform,
});

// 定义请求返回
export interface ResModel {
    str: string;
    num: number;
}
